import { genUiI18n } from './i18n.js';
import type { FlightFilterChipsCopy } from '@voll-intelligence/intelligence-ui-kit';

/** Os textos do `FlightFilterChips` do kit no locale do host. Função pura, sem
 *  Context, como o `flightFilterCopy`: o host memoiza por locale. Cada chave é um
 *  literal — uma chave com erro de digitação ou um eixo novo sem texto reprova o
 *  typecheck aqui, em vez de virar `undefined` na tela. */
export function flightFilterChipsCopy(locale: string | null | undefined): FlightFilterChipsCopy {
  const { t } = genUiI18n(locale);
  return {
    placeholder: t('flightChips.placeholder'),
    send: t('flightChips.send'),
    dictate: t('flightChips.dictate'),
    dictationCancel: t('flightChips.dictationCancel'),
    dictationStop: t('flightChips.dictationStop'),
    dictationSend: t('flightChips.dictationSend'),
    transcribing: t('flightChips.transcribing'),
    dictationFailed: t('flightChips.dictationFailed'),
    applying: t('flightChips.applying'),
    remove: (label) => t('flightChips.remove', { label }),
    legPrefixed: (leg, label) => t('flightChips.leg.prefixed', { leg, label }),
    airline: {
      only: (name) => t('flightChips.airline.only', { name }),
      exclude: (name) => t('flightChips.airline.exclude', { name }),
    },
    stops: {
      nonstop: t('flightChips.stops.nonstop'),
      up_to_1: t('flightChips.stops.up_to_1'),
      up_to_2: t('flightChips.stops.up_to_2'),
      any: t('flightChips.stops.any'),
    },
    time: {
      arriveBefore: (time) => t('flightChips.time.arriveBefore', { time }),
      arriveAfter: (time) => t('flightChips.time.arriveAfter', { time }),
      arriveBetween: (start, end) => t('flightChips.time.arriveBetween', { start, end }),
      departBefore: (time) => t('flightChips.time.departBefore', { time }),
      departAfter: (time) => t('flightChips.time.departAfter', { time }),
      departBetween: (start, end) => t('flightChips.time.departBetween', { start, end }),
    },
    price: {
      max: (price) => t('flightChips.price.max', { price }),
      min: (price) => t('flightChips.price.min', { price }),
      between: (min, max) => t('flightChips.price.between', { min, max }),
    },
    policy: { only: t('flightChips.policy.only'), release: t('flightChips.policy.release') },
    via: {
      avoid: (place) => t('flightChips.via.avoid', { place }),
      through: (place) => t('flightChips.via.through', { place }),
    },
    airport: {
      from: (place) => t('flightChips.airport.from', { place }),
      to: (place) => t('flightChips.airport.to', { place }),
      only: (place) => t('flightChips.airport.only', { place }),
      exclude: (place) => t('flightChips.airport.exclude', { place }),
    },
    sort: {
      rec: t('flightChips.sort.rec'),
      price: t('flightChips.sort.price'),
      dur: t('flightChips.sort.dur'),
      dep: t('flightChips.sort.dep'),
      arr: t('flightChips.sort.arr'),
    },
    axis: {
      airline: t('flightChips.axis.airline'),
      stops: t('flightChips.axis.stops'),
      time: t('flightChips.axis.time'),
      period: t('flightChips.axis.period'),
      price: t('flightChips.axis.price'),
      policy: t('flightChips.axis.policy'),
      via: t('flightChips.axis.via'),
      airport: t('flightChips.axis.airport'),
      sort: t('flightChips.axis.sort'),
      punctuality: t('flightChips.axis.punctuality'),
      baggage: t('flightChips.axis.baggage'),
      seat: t('flightChips.axis.seat'),
      aircraft: t('flightChips.axis.aircraft'),
      layover: t('flightChips.axis.layover'),
      duration: t('flightChips.axis.duration'),
      cabin: t('flightChips.axis.cabin'),
      fare: t('flightChips.axis.fare'),
      other: t('flightChips.axis.other'),
    },
    reason: {
      airport_not_in_search: t('flightChips.reason.airport_not_in_search'),
      airline_not_in_search: t('flightChips.reason.airline_not_in_search'),
      no_punctuality_data: t('flightChips.reason.no_punctuality_data'),
      axis_not_available: t('flightChips.reason.axis_not_available'),
      axis_not_in_panel: t('flightChips.reason.axis_not_in_panel'),
      window_not_contiguous: t('flightChips.reason.window_not_contiguous'),
      low_confidence: t('flightChips.reason.low_confidence'),
      other: t('flightChips.reason.other'),
    },
    note: {
      question: t('flightChips.note.question'),
      new_search: t('flightChips.note.new_search'),
      noise: t('flightChips.note.noise'),
      failed: t('flightChips.note.failed'),
      cabin: (cabin) => t('flightChips.note.cabin', { cabin }),
      cabinSame: (cabin) => t('flightChips.note.cabinSame', { cabin }),
      cabinUnavailable: t('flightChips.note.cabinUnavailable'),
      reset: t('flightChips.note.reset'),
    },
  };
}
